import { type FC, forwardRef, type MouseEventHandler, useState } from 'react';

import { useAtom, useAtomValue } from 'jotai';
import { ChevronLeft, ChevronRight, FileTextIcon, Star } from 'lucide-react';

import { ACCEPTED_MEDIA_TYPES } from '@/features/estate-media/schema/upload-media';
import { cn } from '@/lib/utils';

import { selectedPreviewImageAtom, uploadedImagesAtom } from '../atoms/imageFiles';

interface ImageCarouselProps {
  className?: string;
  imageWrapperClassName?: string | ((active: boolean) => string);
  imageClassName?: string;
  imageWidth?: number;
  showArrowButtons?: boolean;
}

export const ImageRenderingCarousel: FC<ImageCarouselProps> = forwardRef<
  HTMLDivElement,
  ImageCarouselProps
>(
  (
    { className, imageWrapperClassName, imageClassName, imageWidth, showArrowButtons = true },
    ref
  ) => {
    const images = useAtomValue(uploadedImagesAtom);
    const [selectedImage, setSelectedImage] = useAtom(selectedPreviewImageAtom);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [favouriteIds, setFavouriteIds] = useState<string[]>([]);

    const handleFavourite = (id: string) => {
      setFavouriteIds((prev) =>
        prev.includes(id) ? prev.filter((imgId) => imgId !== id) : [...prev, id]
      );
    };

    const nextImage: MouseEventHandler<HTMLButtonElement> = (e) => {
      e.preventDefault();
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    };

    const prevImage: MouseEventHandler<HTMLButtonElement> = (e) => {
      e.preventDefault();
      setCurrentIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
    };

    return (
      <div ref={ref} className="relative mx-auto aspect-square size-full overflow-hidden">
        <div
          className={cn(
            'flex size-full items-center overflow-x-auto transition-transform duration-300 ease-in-out sm:overflow-x-scroll',
            className
          )}
          style={{
            transform: `translateX(-${imageWidth ? `${currentIndex * imageWidth}px` : `${currentIndex * 100}%`})`,
          }}
        >
          {images.map((image) => (
            <button
              type="button"
              key={image.id}
              className={cn(
                'relative flex size-full min-w-28 items-center justify-center',
                imageWrapperClassName
                  ? typeof imageWrapperClassName === 'string'
                    ? imageWrapperClassName
                    : imageWrapperClassName(selectedImage?.id === image.id)
                  : ''
              )}
              style={{ width: imageWidth ? imageWidth : '100%' }}
              onClick={() => {
                setSelectedImage(image);
              }}
            >
              <button
                type="button"
                className="absolute right-2 top-2 z-10 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFavourite(image.id);
                }}
              >
                <Star
                  className={cn(
                    'text-primary-light',
                    favouriteIds.includes(image.id) && 'fill-primary-light'
                  )}
                />
              </button>
              {image?.fileType !== ACCEPTED_MEDIA_TYPES.Pdf ? (
                <img
                  src={image.renderedImage || image.originalImage}
                  alt="carousel"
                  className={cn('max-h-[60vh] max-w-full object-contain', imageClassName)}
                />
              ) : (
                <div className="flex size-full items-center justify-center">
                  <FileTextIcon viewBox="0 0 25 25" width={40} height={40} />
                </div>
              )}
            </button>
          ))}
        </div>
        {showArrowButtons ? (
          <>
            <button
              type="button"
              onClick={prevImage}
              className="absolute left-4 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/50 p-2 text-white sm:block"
            >
              <ChevronLeft className="[&_*]:stroke-primary" />
            </button>
            <button
              type="button"
              onClick={nextImage}
              className="absolute right-4 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/50 p-2 text-white sm:block"
            >
              <ChevronRight className="[&_*]:stroke-primary" />
            </button>
          </>
        ) : null}
      </div>
    );
  }
);
